import { useEffect, useState } from "react";
import { m as motion, useReducedMotion } from "framer-motion";
import { Radar } from "lucide-react";
import { StablePanels } from './StablePanels';

/* Theater of Operations — circular radar/sonar console.
   - HQ sits at the center of a tactical scope.
   - Customer sites are plotted as targets at various bearings & ranges.
   - A rotating sweep arm lights up each target as it passes (auto-cycle).
   - Hovering a site selects it; details show on the right panel. */

const NODES = [
  {
    id: "picatinny",
    bearing: 78, // degrees clockwise from north
    range: 0.88, // 0 (center/HQ) → 1 (rim)
    name: "Picatinny Arsenal",
    region: "NJ · U.S. Army",
    blurb: "PEO Ammunition · joint munitions program support",
  },
  {
    id: "nswc",
    bearing: 95,
    range: 0.82,
    name: "NSWC Indian Head",
    region: "MD · U.S. Navy",
    blurb: "Energetics qualification & shock testing",
  },
  {
    id: "hill",
    bearing: 310,
    range: 0.4,
    name: "Hill AFB",
    region: "UT · U.S. Air Force",
    blurb: "Air-delivered weapons & sustainment programs",
  },
  {
    id: "redstone",
    bearing: 120,
    range: 0.55,
    name: "Redstone Arsenal",
    region: "AL · U.S. Army",
    blurb: "Missile & rocket integration support",
  },
  {
    id: "china-lake",
    bearing: 250,
    range: 0.62,
    name: "NAWS China Lake",
    region: "CA · U.S. Navy",
    blurb: "Weapons development & live-fire testing",
  },
];

const VIEW = 500;
const CX = VIEW / 2;
const CY = VIEW / 2;
const R_MAX = 220;

function bearingToXY(bearing, range) {
  // Bearing: 0 = north (up). Convert to standard math angle.
  const rad = ((bearing - 90) * Math.PI) / 180;
  const r = range * R_MAX;
  return { x: CX + r * Math.cos(rad), y: CY + r * Math.sin(rad) };
}

export default function NationalReach() {
  const reducedMotion = useReducedMotion();
  const [hoverId, setHoverId] = useState(null);
  const [sweep, setSweep] = useState(0); // 0–360
  const [autoId, setAutoId] = useState(NODES[0].id);

  // Continuously rotate the sweep arm
  useEffect(() => {
    if (reducedMotion) return;
    let raf;
    let last = performance.now();
    const tick = (t) => {
      const dt = t - last;
      last = t;
      setSweep((s) => (s + dt * 0.025) % 360); // ~14.5 s per full rotation — calmer cadence
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [reducedMotion]);

  // As the sweep crosses each node's bearing, light it up.
  useEffect(() => {
    if (hoverId) return;
    // Find closest node by bearing to current sweep
    let best = NODES[0];
    let bestDiff = 360;
    for (const n of NODES) {
      const diff = Math.abs(((n.bearing - sweep + 540) % 360) - 180);
      if (diff < bestDiff) {
        bestDiff = diff;
        best = n;
      }
    }
    if (best.id !== autoId) setAutoId(best.id);
  }, [sweep, hoverId, autoId]);

  const activeId = hoverId || autoId;
  const active = NODES.find((n) => n.id === activeId) || NODES[0];

  return (
    <section
      className="relative bg-navy text-ivory overflow-hidden py-20 md:py-24 border-y border-white/10"
      data-testid="national-reach"
    >
      <div className="absolute inset-0 grid-bg-dark opacity-25 pointer-events-none" />
      <div className="absolute inset-0 grain pointer-events-none" />
      <div className="absolute top-0 left-0 right-0 h-px bg-signal/70" />
      <div className="absolute bottom-0 left-0 right-0 h-px bg-signal/20" />

      <div className="relative max-w-[1500px] mx-auto px-6 md:px-10">
        {/* Header */}
        <div className="grid lg:grid-cols-12 gap-8 items-end mb-10 md:mb-14">
          <div className="lg:col-span-8">
            <div className="font-mono text-[14px] tracking-[0.4em] uppercase text-signal mb-5 flex items-center gap-3">
              <span className="w-6 h-px bg-signal" />
              Nationwide Operations · Mission Ready
            </div>
            <h2 className="font-display font-bold text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight">
              Supporting programs from {" "}
              <span className="text-signal">coast to coast.</span>
            </h2>
          </div>
          <div className="lg:col-span-4">
            <p className="text-ivory/70 text-lg leading-[1.6]">
              Supporting programs across the U.S. Army, Navy, and Air Force from our Grand Junction headquarters.
            </p>
          </div>
        </div>

        {/* Console */}
        <div className="grid lg:grid-cols-12 gap-px bg-white/10 border border-white/10">
          {/* RADAR */}
          <div className="lg:col-span-7 relative bg-navy overflow-hidden min-h-[520px] md:min-h-[600px]">
            {/* Bracket corners */}
            <span className="absolute top-4 left-4 w-5 h-5 border-l border-t border-signal/60" />
            <span className="absolute top-4 right-4 w-5 h-5 border-r border-t border-signal/60" />
            <span className="absolute bottom-4 left-4 w-5 h-5 border-l border-b border-signal/60" />
            <span className="absolute bottom-4 right-4 w-5 h-5 border-r border-b border-signal/60" />

            {/* Top HUD strip */}
            <div className="absolute top-5 left-5 right-5 flex items-center justify-between font-mono text-[13px] md:text-[14px] tracking-[0.32em] uppercase text-ivory/70 z-10 pointer-events-none">
              <span className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-signal animate-pulse" />
                Program Map
              </span>
              <span>Grand Junction, CO</span>
              <span className="hidden md:inline">Nationwide Support</span>
            </div>

            {/* Bottom legend */}
            <div className="absolute bottom-5 left-5 right-5 flex items-center justify-between font-mono text-[13px] md:text-[14px] tracking-[0.32em] uppercase text-ivory/80 z-10 pointer-events-none">
              <span className="flex items-center gap-3">
                <span className="inline-block w-2 h-2 bg-signal" />
                Customer Site
              </span>
              <span className="hidden md:inline">Hover · Select a site</span>
              <span className="flex items-center gap-3">
                HQ · Center
                <span className="inline-block w-2.5 h-2.5 border border-signal bg-signal/30 rotate-45" />
              </span>
            </div>

            {/* SVG radar */}
            <svg
              viewBox={`0 0 ${VIEW} ${VIEW}`}
              className="w-full h-full"
              preserveAspectRatio="xMidYMid meet"
              aria-hidden
            >
              <defs>
                <radialGradient id="scope-bg" cx="50%" cy="50%" r="50%">
                  <stop offset="0%" stopColor="#0B1530" stopOpacity="1" />
                  <stop offset="60%" stopColor="#091128" stopOpacity="1" />
                  <stop offset="100%" stopColor="#070D1F" stopOpacity="1" />
                </radialGradient>
                <radialGradient id="hq-glow" cx="50%" cy="50%" r="50%">
                  <stop offset="0%" stopColor="#E61C24" stopOpacity="0.35" />
                  <stop offset="100%" stopColor="#E61C24" stopOpacity="0" />
                </radialGradient>
                <linearGradient
                  id="sweep-grad"
                  x1={CX}
                  y1={CY}
                  x2={CX}
                  y2={CY - R_MAX}
                  gradientUnits="userSpaceOnUse"
                >
                  <stop offset="0%" stopColor="#E61C24" stopOpacity="0.7" />
                  <stop offset="100%" stopColor="#E61C24" stopOpacity="0" />
                </linearGradient>
              </defs>

              {/* Scope background */}
              <circle cx={CX} cy={CY} r={R_MAX + 20} fill="url(#scope-bg)" />
              <circle cx={CX} cy={CY} r={R_MAX} fill="url(#hq-glow)" />

              {/* Range rings */}
              {[0.25, 0.5, 0.75, 1].map((r) => (
                <circle
                  key={r}
                  cx={CX}
                  cy={CY}
                  r={r * R_MAX}
                  fill="none"
                  stroke="rgba(230,28,36,0.18)"
                  strokeWidth={r === 1 ? "1.2" : "0.7"}
                  strokeDasharray={r === 1 ? "0" : "2 4"}
                />
              ))}

              {/* Crosshair */}
              <line
                x1={CX - R_MAX}
                y1={CY}
                x2={CX + R_MAX}
                y2={CY}
                stroke="rgba(255,255,255,0.12)"
                strokeWidth="0.8"
              />
              <line
                x1={CX}
                y1={CY - R_MAX}
                x2={CX}
                y2={CY + R_MAX}
                stroke="rgba(255,255,255,0.12)"
                strokeWidth="0.8"
              />

              {/* Compass marks N/E/S/W */}
              {[
                { lbl: "N", x: CX, y: CY - R_MAX - 8, anchor: "middle" },
                { lbl: "E", x: CX + R_MAX + 10, y: CY + 4, anchor: "start" },
                { lbl: "S", x: CX, y: CY + R_MAX + 16, anchor: "middle" },
                { lbl: "W", x: CX - R_MAX - 10, y: CY + 4, anchor: "end" },
              ].map((c) => (
                <text
                  key={c.lbl}
                  x={c.x}
                  y={c.y}
                  textAnchor={c.anchor}
                  fontFamily="ui-monospace, monospace"
                  fontSize="11"
                  letterSpacing="3"
                  fill="rgba(230,28,36,0.7)"
                >
                  {c.lbl}
                </text>
              ))}

              {/* Sweep arm */}
              <g transform={`rotate(${sweep} ${CX} ${CY})`}>
                {/* Wedge — clip a sector to give the sweep volume */}
                <path
                  d={`M ${CX} ${CY} L ${CX} ${CY - R_MAX} A ${R_MAX} ${R_MAX} 0 0 1 ${
                    CX + R_MAX * Math.sin((35 * Math.PI) / 180)
                  } ${CY - R_MAX * Math.cos((35 * Math.PI) / 180)} Z`}
                  fill="url(#sweep-grad)"
                  opacity="0.55"
                />
                {/* Sharp leading edge */}
                <line
                  x1={CX}
                  y1={CY}
                  x2={CX}
                  y2={CY - R_MAX}
                  stroke="#E61C24"
                  strokeWidth="1.4"
                />
              </g>

              {/* Speckle stations / minor pings on the grid */}
              {Array.from({ length: 28 }).map((_, i) => {
                const a = (i * 73) % 360;
                const r = (((i * 41) % 100) / 100) * (R_MAX - 14) + 8;
                const rad = ((a - 90) * Math.PI) / 180;
                const x = CX + r * Math.cos(rad);
                const y = CY + r * Math.sin(rad);
                return (
                  <circle
                    key={`spk-${i}`}
                    cx={x}
                    cy={y}
                    r="1"
                    fill="rgba(255,255,255,0.16)"
                  />
                );
              })}

              {/* Target nodes */}
              {NODES.map((n) => {
                const { x, y } = bearingToXY(n.bearing, n.range);
                const isActive = n.id === activeId;
                return (
                  <g
                    key={n.id}
                    onMouseEnter={() => setHoverId(n.id)}
                    onMouseLeave={() => setHoverId((cur) => (cur === n.id ? null : cur))}
                    style={{ cursor: "pointer" }}
                    data-testid={`reach-node-${n.id}`}
                  >
                    {/* Hit area */}
                    <circle cx={x} cy={y} r="22" fill="transparent" pointerEvents="all" />
                    {/* Pulse halo */}
                    {isActive && (
                      <motion.circle
                        cx={x}
                        cy={y}
                        r="6"
                        fill="none"
                        stroke="#E61C24"
                        strokeWidth="1.5"
                        initial={{ scale: 1, opacity: 0.9 }}
                        animate={{ scale: 3.5, opacity: 0 }}
                        transition={{ duration: 1.6, repeat: Infinity, ease: "easeOut" }}
                      />
                    )}
                    {/* Reticle when active */}
                    {isActive && (
                      <g
                        stroke="#E61C24"
                        strokeWidth="1.2"
                        fill="none"
                        opacity="0.85"
                      >
                        <line x1={x - 14} y1={y} x2={x - 8} y2={y} />
                        <line x1={x + 8} y1={y} x2={x + 14} y2={y} />
                        <line x1={x} y1={y - 14} x2={x} y2={y - 8} />
                        <line x1={x} y1={y + 8} x2={x} y2={y + 14} />
                      </g>
                    )}
                    {/* Core dot */}
                    <circle
                      cx={x}
                      cy={y}
                      r={isActive ? "5.5" : "3.8"}
                      fill={isActive ? "#E61C24" : "rgba(230,28,36,0.85)"}
                      stroke={isActive ? "#ffffff" : "rgba(255,255,255,0.45)"}
                      strokeWidth="1"
                      style={{ transition: "r 0.3s, fill 0.3s" }}
                    />
                    {/* Tiny label */}
                    {isActive && (
                      <text
                        x={x + 14}
                        y={y - 8}
                        fontFamily="ui-monospace, monospace"
                        fontSize="9"
                        letterSpacing="1.5"
                        fill="#ffffff"
                        opacity="0.9"
                      >
                        {n.id.toUpperCase().slice(0, 7)}
                      </text>
                    )}
                  </g>
                );
              })}

              {/* HQ marker — diamond at center */}
              <g>
                <rect
                  x={CX - 9}
                  y={CY - 9}
                  width="18"
                  height="18"
                  fill="#E61C24"
                  transform={`rotate(45 ${CX} ${CY})`}
                />
                <rect
                  x={CX - 5}
                  y={CY - 5}
                  width="10"
                  height="10"
                  fill="#ffffff"
                  transform={`rotate(45 ${CX} ${CY})`}
                />
              </g>
            </svg>
          </div>

          {/* SIDE READOUT */}
          <div className="lg:col-span-5 bg-navy p-6 md:p-8 relative flex flex-col">
            <div className="absolute top-0 left-0 w-12 h-[2px] bg-signal" />

            <div className="font-mono text-[14px] tracking-[0.32em] uppercase text-signal mb-5 flex items-center gap-3">
              <Radar className="w-3.5 h-3.5" strokeWidth={1.7} />
              Site Details
            </div>

            <StablePanels items={NODES} activeIndex={NODES.findIndex(node => node.id === active.id)} data-testid="reach-panels" renderPanel={active => (
              <div
                data-testid={`reach-brief-${active.id}`}
              >
                <div className="font-mono text-[14px] tracking-[0.32em] uppercase text-ivory/80 mb-3">
                  {active.region}
                </div>
                <h3 className="font-display font-bold text-2xl md:text-3xl text-ivory leading-[1.05] tracking-tight">
                  {active.name}
                </h3>
                <p className="mt-4 text-ivory/80 text-[15px] leading-[1.65]">
                  {active.blurb}
                </p>

                <div className="mt-6 pt-5 border-t border-white/15 grid grid-cols-3 gap-4 font-mono text-[14px] tracking-[0.22em] uppercase">
                  <div>
                    <div className="text-ivory/75 mb-1">Bearing</div>
                    <div className="text-ivory tabular-nums text-base font-display">
                      {String(active.bearing).padStart(3, "0")}°
                    </div>
                  </div>
                  <div>
                    <div className="text-ivory/75 mb-1">Range</div>
                    <div className="text-ivory tabular-nums text-base font-display">
                      {(active.range * 100).toFixed(0)}%
                    </div>
                  </div>
                  <div>
                    <div className="text-ivory/75 mb-1">Status</div>
                    <div className="text-signal text-base font-display flex items-center gap-2">
                      <span className="w-1.5 h-1.5 bg-signal animate-pulse" />
                      Live
                    </div>
                  </div>
                </div>
              </div>
            )} />

            {/* Bottom stats */}
            <div data-testid="reach-summary-stats" className="mt-auto pt-8 border-t border-white/15 grid grid-cols-3 gap-3 text-center">
              {[
                { v: "05", l: "Sites" },
                { v: "03", l: "Branches" },
                { v: "100%", l: "U.S." },
              ].map((s) => (
                <div key={s.l}>
                  <div className="font-display font-bold text-2xl md:text-3xl text-ivory leading-none tabular-nums">
                    {s.v}
                  </div>
                  <div className="mt-1 font-mono text-[14px] tracking-[0.28em] uppercase text-ivory/78">
                    {s.l}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
